import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type {
    OrderItemId} from "@@/shared/orders/order-items/order-item-id";
import {
    orderItemIdZodSchema,
} from "@@/shared/orders/order-items/order-item-id";
import type { ProductId} from "@@/shared/products/product-id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    translatableZodSchema,
} from "@@/shared/translations/translation";
import {
    type SystemUser,
    systemUserZodSchema,
} from "@@/shared/users/system-user";
import { type UserId, userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import z from "zod";
import { orderChangeIdFactory } from "./_order-change-id";
import { orderChangeSchemaFactory } from "./_order-change.schema-factory";

const orderItemProductChaangedOrderChangeDataZodObject: z.ZodType<{
    changedBy: UserId | SystemUser;
    orderItemId: OrderItemId;
    from: { id: ProductId; title: Translatable };
    to: { id: ProductId; title: Translatable };
    type: "ORDER_ITEM_PRODUCT_CHANGED";
}> = z.object({
    changedBy: userIdZodSchema.or(systemUserZodSchema),
    orderItemId: orderItemIdZodSchema,
    type: z.literal("ORDER_ITEM_PRODUCT_CHANGED"),
    from: z.object({
        id: productIdZodSchema,
        title: translatableZodSchema,
    }),
    to: z.object({
        id: productIdZodSchema,
        title: translatableZodSchema,
    }),
});
const orderItemProductOrderChangeZodObject = orderChangeSchemaFactory(
    "ORDER_ITEM_PRODUCT_CHANGED",
    orderItemProductChaangedOrderChangeDataZodObject,
);

type OrderItemProductOrderChange = z.infer<
    typeof orderItemProductOrderChangeZodObject
>;
const orderItemProductOrderChangeZodSchema =
    orderItemProductOrderChangeZodObject as z.ZodSchema<OrderItemProductOrderChange>;

type FactoryParams = Except<
    OrderItemProductOrderChange,
    "_id" | "_createdAt" | "_type" | "type"
>;
const orderItemProductOrderChangeFactory = (
    params: FactoryParams,
): OrderItemProductOrderChange => {
    const orderChange: OrderItemProductOrderChange = {
        ...params,
        _id: orderChangeIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "ORDER_CHANGE",
        type: "ORDER_ITEM_PRODUCT_CHANGED",
    };
    return orderItemProductOrderChangeZodSchema.parse(orderChange);
};

const isOrderItemProductOrderChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    orderChange: any,
): orderChange is OrderItemProductOrderChange => {
    return (
        orderChange._type === "ORDER_CHANGE" &&
        orderChange.type === "ORDER_ITEM_PRODUCT_CHANGED"
    );
};

export {
    isOrderItemProductOrderChange,
    orderItemProductOrderChangeFactory,
    orderItemProductOrderChangeZodObject,
    orderItemProductOrderChangeZodSchema,
};
export type { OrderItemProductOrderChange };
